import { useLayoutEffect, useRef, useState } from "react";

import { doorMergePath } from "~/mergeTabGeometry";

/** The mark's identity read (TH5 direct terra read), shared by stroke and bloom. */
const ACCENT = "var(--ncl-world-accent, var(--ncl-accent, var(--primary)))";

/**
 * The merged-tab active mark (FD3/FD6): the selected tab fuses downward into
 * the row beneath it, drawn by the one geometry law (FD2's module) at the
 * tab's measured size, stroked through the TH5 direct terra read. Fills its
 * offset parent (the tab/door element itself — position it relative).
 *
 * One mark per row: rows that adopt this retire their sliding underline
 * (the Worlds row did 2026-09-01; the World root Session row followed the
 * same day under FD-CAN-06's dated NV1 revision). Nested pill rows keep
 * their sliding filled pill — a different vocabulary, untouched.
 */
export function MergeTabMark({
  dataCleo,
  top = 2.5,
  treated = false,
}: {
  readonly dataCleo: string;
  /**
   * The mark's top line inside the host box (the door-merge path's `top`).
   * The deck strip passes 0.5 so the degrade mark stands exactly where
   * RailCard's carve line does (dated 2026-09-02); other rows keep 2.5.
   */
  readonly top?: number;
  /**
   * FD27 (register FD-CAN-27 as refined): the design's fill-and-bloom
   * treatment — the bump fills with a surface-token color-mix (the
   * design's .62 bump fill) and the silhouette blooms via drop-shadow.
   * Opt-in per wearer; the deck's active surface tab wears it first,
   * other rows follow at the principal's witness.
   */
  readonly treated?: boolean;
}) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 140, h: 30 });
  useLayoutEffect(() => {
    const host = ref.current?.parentElement;
    if (!host || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const { offsetWidth, offsetHeight } = host;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setSize((prev) =>
          prev.w === offsetWidth && prev.h === offsetHeight
            ? prev
            : { w: offsetWidth, h: offsetHeight },
        );
      }
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-visible forced-colors:hidden"
      data-cleo={dataCleo}
      data-treated={treated ? "" : undefined}
      fill="none"
      height={size.h}
      preserveAspectRatio="none"
      ref={ref}
      style={
        treated
          ? { filter: `drop-shadow(0 0 5px color-mix(in oklab, ${ACCENT} 30%, transparent))` }
          : undefined
      }
      viewBox={`0 0 ${size.w} ${size.h}`}
      width={size.w}
    >
      <path
        d={doorMergePath(size.w, size.h, top)}
        fill={
          treated
            ? "color-mix(in oklab, var(--app-theme-surface-raised, var(--ncl-surface-raised)) 62%, transparent)"
            : "none"
        }
        stroke={ACCENT}
        strokeOpacity="0.55"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
